import {
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Shape,
  ShapeGeometry,
  type Material,
  type Texture,
} from 'three';
import type { Card } from '../../engine/card.js';
import { uvForBack, uvForFace } from './card-faces.js';
import { SHADOW_CASTER_LAYER } from './shadow-pass.js';

export const CARD_WIDTH = 1;
export const CARD_HEIGHT = 1.4;
export const CARD_DEPTH = 0.008;
const CORNER_RADIUS = 0.06;
const FACE_OFFSET = 0.0006;

export interface CardMaterials {
  front: Material;
  back: Material;
  edge: Material;
}

function roundedRectShape(w: number, h: number, r: number): Shape {
  const s = new Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

function remapShapeUVs(geometry: ShapeGeometry) {
  const uv = geometry.attributes.uv;
  if (!uv) return;
  for (let i = 0; i < uv.count; i++) {
    const x = uv.getX(i);
    const y = uv.getY(i);
    uv.setXY(i, (x + CARD_WIDTH / 2) / CARD_WIDTH, (y + CARD_HEIGHT / 2) / CARD_HEIGHT);
  }
  uv.needsUpdate = true;
}

let _shape: Shape | null = null;
function shape(): Shape {
  if (!_shape) _shape = roundedRectShape(CARD_WIDTH, CARD_HEIGHT, CORNER_RADIUS);
  return _shape;
}

let _edgeGeom: ExtrudeGeometry | null = null;
function edgeGeometry(): ExtrudeGeometry {
  if (!_edgeGeom) {
    _edgeGeom = new ExtrudeGeometry(shape(), { depth: CARD_DEPTH, bevelEnabled: false });
    _edgeGeom.translate(0, 0, -CARD_DEPTH / 2);
  }
  return _edgeGeom;
}

function cloneAtlas(atlas: Texture, offset: readonly [number, number], repeat: readonly [number, number]): Texture {
  const t = atlas.clone();
  t.needsUpdate = true;
  t.offset.set(offset[0], offset[1]);
  t.repeat.set(repeat[0], repeat[1]);
  return t;
}

export function createCardFront(atlas: Texture, card: Card): Material {
  const { offset, repeat } = uvForFace(card);
  const map = cloneAtlas(atlas, offset, repeat);
  return new MeshStandardMaterial({ map, roughness: 0.45, metalness: 0 });
}

export function createCardBack(atlas: Texture): Material {
  const { offset, repeat } = uvForBack();
  const map = cloneAtlas(atlas, offset, repeat);
  return new MeshStandardMaterial({ map, roughness: 0.5, metalness: 0 });
}

export function createEdgeMaterial(): Material {
  return new MeshStandardMaterial({ color: 0xfafafa, roughness: 0.8, metalness: 0 });
}

export function createCardMesh(materials: CardMaterials): Group {
  const group = new Group();

  const frontGeom = new ShapeGeometry(shape());
  remapShapeUVs(frontGeom);
  const frontMesh = new Mesh(frontGeom, materials.front);
  frontMesh.rotation.x = -Math.PI / 2;
  frontMesh.position.y = CARD_DEPTH / 2 + FACE_OFFSET;
  group.add(frontMesh);

  const backGeom = new ShapeGeometry(shape());
  remapShapeUVs(backGeom);
  const backMesh = new Mesh(backGeom, materials.back);
  backMesh.rotation.x = Math.PI / 2;
  backMesh.position.y = -CARD_DEPTH / 2 - FACE_OFFSET;
  group.add(backMesh);

  const transparent = new MeshBasicMaterial({ transparent: true, opacity: 0 });
  const edgeMesh = new Mesh(edgeGeometry(), [transparent, transparent, materials.edge]);
  edgeMesh.rotation.x = -Math.PI / 2;
  group.add(edgeMesh);

  // Cards opt into the shadow-caster layer so the top-down shadow pass picks
  // them up. Real `castShadow`/`receiveShadow` stay off - the shadow pass is
  // the only mechanism that actually puts dark pixels on the felt.
  group.traverse((c) => c.layers.enable(SHADOW_CASTER_LAYER));

  return group;
}
